/**
 * Style fragments every Ledger recipe shares, so a state looks the same on a
 * button, an input and a checkbox. Values are `tokens.css` variables only.
 *
 * Spec: docs/design-system/DESIGN-SYSTEM.md, "Accessibility and keyboard" and
 * "Components 2: forms".
 */

/**
 * The focus ring: 2px of page colour, then 2px of accent, drawn outside the
 * control as a box-shadow so a row or a table container never clips it. Only
 * `:focus-visible`, so a mouse press draws nothing.
 */
export const focusRing = {
  outline: "none",
  boxShadow: "var(--color-focus-ring)",
} as const;

/**
 * The ring for controls whose focusable element is a hidden input (checkbox,
 * radio, switch, segmented item): the visible part draws it when the input
 * inside reports focus.
 */
export const focusRingWithin = {
  "&:has(input:focus-visible)": focusRing,
} as const;

/** A disabled control: disabled text on surface-3, no hover, no press. */
export const disabledControl = {
  opacity: 1,
  cursor: "not-allowed",
  color: "var(--color-fg-disabled)",
  background: "var(--color-surface-3)",
  borderColor: "var(--color-border-2)",
} as const;

/**
 * Text-entry controls (input, textarea, select trigger, native select):
 * surface fill, input border at rest, accent border plus the ring on focus,
 * err border when invalid, surface-3 fill when disabled.
 */
export const fieldControl = {
  background: "var(--color-surface)",
  color: "var(--color-fg)",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "var(--color-border-input)",
  borderRadius: "var(--radius-2)",
  fontSize: "var(--text-13)",
  lineHeight: "var(--leading-normal)",
  transitionProperty: "border-color, box-shadow, background-color",
  transitionDuration: "var(--dur-1)",
  transitionTimingFunction: "var(--ease-out)",
  _placeholder: { color: "var(--color-fg-placeholder)", opacity: 1 },
  _hover: { borderColor: "var(--color-fg-3)" },
  _focusVisible: {
    ...focusRing,
    borderColor: "var(--color-accent)",
  },
  _invalid: {
    borderColor: "var(--color-err)",
    _focusVisible: { borderColor: "var(--color-err)" },
  },
  _disabled: disabledControl,
  _readOnly: { background: "var(--color-surface-2)" },
} as const;

/** The three control heights, by the Chakra size names the app already uses. */
export const controlHeight = {
  "2xs": "var(--control-sm)",
  xs: "var(--control-sm)",
  sm: "var(--control-md)",
  md: "var(--control-md)",
  lg: "var(--control-lg)",
  xl: "var(--control-lg)",
  "2xl": "var(--control-lg)",
} as const;

/**
 * The 32px press area below 1024 for a control drawn smaller than 32px (a 24
 * or 28px button, a 28px tab): an invisible centred box of
 * `max(100%, var(--hit-min))` both ways, the step 3 hit area, drawn by
 * `::after` so nothing in the layout moves. From 1024 `--press-area` is 0 and
 * the box is the control itself. A control that sets its own `_after` hit
 * area keeps it (IconButton's, which is always on; the shell's
 * `COMPACT_PRESS_AREA`): its width, height and display win over these.
 *
 * docs/design-system/DEVIATIONS.md, P-20.
 */
export const compactPressArea = {
  position: "relative",
  "--press-area": "var(--hit-min)",
  "@media screen and (min-width: 1024px)": { "--press-area": "0px" },
  _after: {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "max(100%, var(--press-area))",
    height: "max(100%, var(--press-area))",
    transform: "translate(-50%, -50%)",
  },
} as const;

/** A floating list (select, combobox): surface, 1px border, radius 4, shadow-2. */
export const floatingList = {
  background: "var(--color-surface)",
  color: "var(--color-fg)",
  border: "1px solid",
  borderColor: "var(--color-border)",
  borderRadius: "var(--radius-3)",
  boxShadow: "var(--shadow-2)",
  backdropFilter: "none",
  padding: "var(--space-1)",
} as const;

/** One option row in a floating list: 28px, hover and highlight on surface-3. */
export const listOption = {
  minHeight: "var(--control-md)",
  paddingInline: "var(--space-2)",
  paddingBlock: "0",
  marginInline: "0",
  borderRadius: "var(--radius-2)",
  fontSize: "var(--text-13)",
  color: "var(--color-fg)",
  _highlighted: { background: "var(--color-surface-3)" },
  _hover: { background: "var(--color-surface-3)" },
  _selected: { fontWeight: "var(--weight-medium)" },
  _disabled: { color: "var(--color-fg-disabled)", opacity: 1 },
  _icon: { width: "var(--icon-sm)", height: "var(--icon-sm)" },
} as const;
